// Escritura atómica de los formularios discretos: en vez de lanzar `createResource` / `updateResource` sueltos (con
// `Promise.all` o en serie) —donde un fallo a mitad deja recursos creados y el reintento los duplica— cada formulario
// junta todo en un `LoteTransaccional` y lo envía con UNA transacción (`executeBatch`, Bundle `type: 'transaction'`),
// el mismo patrón de `guardadoAnamnesis.ts`. Todo se guarda o nada. Las lecturas previas (p. ej. leer el Encounter para
// actualizarlo) y las validaciones se hacen ANTES: `enviar` es lo último que se ejecuta.
import type { MedplumClient } from '@medplum/core';
import type { Bundle, BundleEntry, Resource } from '@medplum/fhirtypes';
import i18n from '../../i18n';
import { notify } from '@/components/ui/sonner';

/**
 * Estados del `Encounter` en los que el servidor ya NO admite ediciones (D-052, «Cierre clínico»): `finished`, `cancelled` y `entered-in-error`. Espeja la regla
 * `encounter-cerrado` / `encounter-anulada` de `packages/web/src/api/lib/hce-guardas-escritura.ts`; si cambian allí, cambian aquí.
 */
export const ESTADOS_ENCOUNTER_CERRADO: readonly string[] = ['finished', 'cancelled', 'entered-in-error'];
export const encounterCerrado = (e: { status?: string } | null | undefined): boolean => !!e?.status && ESTADOS_ENCOUNTER_CERRADO.includes(e.status);

export class LoteTransaccional {
  private readonly entradas: BundleEntry[] = [];
  /** `true` si `actualizarEncounterAbierto` omitió el PUT porque la consulta ya estaba cerrada: `enviar` avisa al usuario cuando el guardado termina bien. */
  encounterCerradoOmitido = false;

  /**
   * Agrega un POST al lote y devuelve el mismo recurso (para poder seguir usándolo sin esperar a la red).
   * Si otro recurso del mismo lote lo referencia, pasa un `fullUrl` (`idTemporal()`) y refiérelo como ese `urn:uuid:…`:
   * el servidor lo sustituye por el id real al crearlo.
   */
  crear<T extends Resource>(recurso: T, fullUrl?: string): T {
    this.entradas.push({ ...(fullUrl ? { fullUrl } : {}), resource: recurso, request: { method: 'POST', url: recurso.resourceType } });
    return recurso;
  }

  /** Agrega un PUT (el recurso debe traer `id`, p. ej. el Encounter recién leído). */
  actualizar<T extends Resource & { id: string }>(recurso: T): T {
    this.entradas.push({ resource: recurso, request: { method: 'PUT', url: `${recurso.resourceType}/${recurso.id}` } });
    return recurso;
  }

  /**
   * PUT de un `Encounter` para anotarle motivo, nivel de cuidado u otros datos que un formulario aporta a la consulta. Si la consulta ya está CERRADA
   * (`finished`/`cancelled`/anulada) el servidor rechazaría el PUT y, al ser transacción, perderíamos TAMBIÉN el formulario entero: se omite y el resto se guarda.
   * Devuelve `false` si se omitió, y `enviar` avisa al usuario (toast) de que el motivo / nivel de cuidado no se anotó en la atención cerrada. La edición del estado y las fechas de la atención NO usa este método (es `Formulario001Page`, que avisa si está cerrada).
   */
  actualizarEncounterAbierto<T extends Resource & { id: string; status?: string }>(recurso: T): boolean {
    if (encounterCerrado(recurso)) {
      this.encounterCerradoOmitido = true;
      return false;
    }
    this.actualizar(recurso);
    return true;
  }

  get tamano(): number {
    return this.entradas.length;
  }

  /** Envía todo en una sola transacción y devuelve la respuesta (una entrada por entrada del lote, en el mismo orden); sin entradas no llama a la red. */
  async enviar(medplum: MedplumClient): Promise<Bundle | undefined> {
    if (this.entradas.length === 0) return undefined;
    const bundle: Bundle = { resourceType: 'Bundle', type: 'transaction', entry: this.entradas };
    const respuesta = await medplum.executeBatch(bundle);
    // Si el servidor no tratara el Bundle como transacción, las entradas fallidas vendrían dentro de un 200: se convierte en error.
    const fallida = respuesta.entry?.find((e) => !e.response?.status?.startsWith('2'));
    if (fallida) throw new Error(`Falló el guardado (${fallida.response?.status ?? 'sin estado'}). No se guardó todo el formulario.`);
    // executeBatch no invalida las búsquedas en caché del cliente (createResource sí): se hace aquí.
    // Tampoco deja en caché el recurso actualizado (updateResource sí): un readReference posterior devolvería la versión vieja.
    for (const e of this.entradas) if (e.request?.method === 'PUT' && e.resource?.id) medplum.invalidateUrl(medplum.fhirUrl(e.resource.resourceType, e.resource.id));
    for (const tipo of new Set(this.entradas.map((e) => e.resource?.resourceType))) if (tipo) medplum.invalidateSearches(tipo);
    if (this.encounterCerradoOmitido) notify({ color: 'yellow', message: i18n.t('formularios.f001.encounterCerradoNoAnotado'), autoClose: 10000 });
    return respuesta;
  }
}

/** Identificador temporal `urn:uuid:…` para referenciar, dentro del mismo lote, un recurso que aún no tiene id. */
export function idTemporal(): string {
  return `urn:uuid:${crypto.randomUUID()}`;
}

/** Recurso creado que devolvió el servidor para la entrada `indice` del lote; si la respuesta no lo trae, el local. */
export function recursoRespuesta<T extends Resource>(respuesta: Bundle | undefined, indice: number, local: T): T {
  return (respuesta?.entry?.[indice]?.resource as T | undefined) ?? local;
}
